import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { Card } from '@/components/ui/card';
import { EmptyState, ListSkeleton, TableSkeleton } from '@/components/ui/feedback';
import { Table, TableToolbar, TableWrap, TBody, TD, TH, THead, TR, TablePagination, ToolbarField } from '@/components/ui/table';
import { Combobox } from '@/components/ui/combobox';
import { adminUsagePageQuery } from '@/lib/queries';
import { formatCount, formatMonth } from '@/lib/format';

/** Rows per page, matching the portal's tables. */
const PAGE_SIZE = 10;

const searchSchema = z.object({
  month: z
    .string()
    .regex(/^[0-9]{4}-[0-9]{2}$/)
    .optional(),
});

function recentMonths(count: number): string[] {
  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  });
}

export const Route = createFileRoute('/_app/admin/usage')({
  // The shape this page takes, rather than a search box over a table for
  // every route: the screen must not rearrange itself once the data lands.
  pendingComponent: () => <ListSkeleton rows={6} columns={5} />,
  validateSearch: searchSchema,
  component: AdminUsagePage,
});

function AdminUsagePage() {
  const { month } = Route.useSearch();
  const navigate = Route.useNavigate();
  const months = recentMonths(12);
  const selected = month ?? months[0] ?? '2026-02';
  const [page, setPage] = useState(1);
  const usage = useQuery(adminUsagePageQuery(selected, page, PAGE_SIZE));

  const items = usage.data?.items ?? [];
  const totals = items.reduce(
    (sum, row) => ({
      recordsChecked: sum.recordsChecked + row.recordsChecked,
      recordsFiled: sum.recordsFiled + row.recordsFiled,
      transmissions: sum.transmissions + row.transmissions,
    }),
    { recordsChecked: 0, recordsFiled: 0, transmissions: 0 },
  );

  return (
    <Card data-tour="admin-usage">
      <TableToolbar>
        <ToolbarField label="Month" className="w-[220px]">
          <div data-tour="admin-usage-month">
          <Combobox
            ariaLabel="Month"
            value={selected}
            onValueChange={(value) => void navigate({ search: { month: value } })}
            options={months.map((value) => ({ value, label: formatMonth(value) }))}
          />
          </div>
        </ToolbarField>
      </TableToolbar>

      {usage.isPending ? (
        <TableSkeleton rows={4} columns={4} />
      ) : items.length === 0 ? (
        <EmptyState
          title="Nothing recorded for that month"
          description="Select another month, or check back once e-files start."
        />
      ) : (
        <>
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <TH>Customer</TH>
                  <TH align="right">Records checked</TH>
                  <TH align="right">Records filed</TH>
                  <TH align="right">Transmissions</TH>
                </tr>
              </THead>
              <TBody>
                {items.map((row) => (
                  <TR key={row.customerId}>
                    <TD className="font-medium">{row.customerName ?? row.customerId}</TD>
                    <TD numeric>{formatCount(row.recordsChecked)}</TD>
                    <TD numeric>{formatCount(row.recordsFiled)}</TD>
                    <TD numeric>{formatCount(row.transmissions)}</TD>
                  </TR>
                ))}
                <TR className="font-medium">
                  <TD>Total</TD>
                  <TD numeric>{formatCount(totals.recordsChecked)}</TD>
                  <TD numeric>{formatCount(totals.recordsFiled)}</TD>
                  <TD numeric>{formatCount(totals.transmissions)}</TD>
                </TR>
              </TBody>
            </Table>
          </TableWrap>
          <TablePagination
            page={page}
            pageSize={PAGE_SIZE}
            total={usage.data?.total ?? usage.data?.items?.length ?? 0}
            busy={usage.isFetching}
            onPageChange={setPage}
          />
        </>
      )}
    </Card>
  );
}
